import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Card,
  Stack,
  Box,
  Skeleton,
  List,
  ListItemButton,
  Chip,
} from "@mui/material";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import KendoInput from "assets/theme/components/inputKendo";
import { PartsApiService } from "api/organizations.api";

const containerStyles = {
  width: { xs: "96%", md: "60%", lg: "45%" },
  maxHeight: "82vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: 2,
  p: 3,
};

const AddPartPickerModal = ({ open, handleClose, onAddItem }) => {
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [parts, setParts] = useState([]);
  const [quantity, setQuantity] = useState("1");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        setLoading(true);
        const response = await PartsApiService.getParts({
          keyword,
          pageNumber: 1,
          pageSize: 10,
        });
        if (cancelled) return;
        setParts(response?.data?.data?.data ?? []);
      } catch (error) {
        if (cancelled) return;
        toast.error(error?.response?.data?.message || "Failed to load parts");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [open, keyword]);

  useEffect(() => {
    if (!open) {
      setKeyword("");
      setQuantity("1");
      setSelected(null);
      setParts([]);
    }
  }, [open]);

  const rate = useMemo(() => Number(selected?.unitCost ?? 0), [selected]);
  const qtyNum = Math.max(0, Number(quantity) || 0);
  const insufficient =
    selected && typeof selected.availableQuantity === "number" && qtyNum > selected.availableQuantity;

  const handleAdd = () => {
    if (!selected) {
      toast.error("Pick a part first.");
      return;
    }
    if (qtyNum <= 0) {
      toast.error("Quantity must be greater than zero.");
      return;
    }
    onAddItem({
      lineKind: "part",
      partId: selected.id,
      name: selected.name,
      partNumber: selected.partNumber,
      itemNo: selected.partNumber || String(selected.id),
      description: selected.description || "",
      quantity: String(qtyNum),
      rate: String(rate),
      price: String(rate),
      partLocationName: selected.inventoryLocationName,
      partAvailableQuantity: selected.availableQuantity,
    });
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <SoftBox sx={containerStyles}>
        <Card sx={{ p: 2, boxShadow: "none" }}>
          <SoftTypography variant="h5" mb={2}>
            Add Part to Quotation
          </SoftTypography>

          <KendoInput
            placeholder="Search parts by name or part number…"
            type="search"
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setSelected(null);
            }}
          />

          <SoftBox mt={2} sx={{ minHeight: 240 }}>
            {loading ? (
              <Skeleton variant="rectangular" height={240} animation="wave" />
            ) : parts.length === 0 ? (
              <SoftBox p={2} textAlign="center">
                <SoftTypography variant="caption">No matching parts.</SoftTypography>
              </SoftBox>
            ) : (
              <List dense sx={{ maxHeight: 280, overflowY: "auto", border: "1px solid #eee", borderRadius: 1 }}>
                {parts.map((p) => {
                  const isSelected = selected?.id === p.id;
                  const belowMin =
                    typeof p.minimumQuantity === "number" &&
                    (p.availableQuantity ?? 0) < p.minimumQuantity;
                  return (
                    <ListItemButton
                      key={p.id}
                      selected={isSelected}
                      onClick={() => setSelected(p)}
                      sx={{
                        borderBottom: "1px solid #f0f0f0",
                        "&.Mui-selected": { backgroundColor: "#E3F2FD" },
                      }}
                    >
                      <SoftBox flex={1}>
                        <SoftTypography variant="body2" fontWeight="medium" fontSize={13}>
                          {p.name} {p.partNumber ? `(${p.partNumber})` : ""}
                        </SoftTypography>
                        <SoftBox display="flex" alignItems="center" gap={1} mt={0.5}>
                          <SoftTypography variant="caption" color="text">
                            {p.unitCost != null ? `$${p.unitCost}` : "$0"}
                          </SoftTypography>
                          <SoftTypography variant="caption" color="text">
                            • {p.availableQuantity ?? 0} on hand
                            {p.inventoryLocationName ? ` @ ${p.inventoryLocationName}` : ""}
                          </SoftTypography>
                          {belowMin && (
                            <Chip
                              label="Below min"
                              size="small"
                              sx={{
                                height: 18,
                                backgroundColor: "#FFF4CE",
                                color: "#7A5A00",
                                fontWeight: 600,
                                fontSize: 10,
                              }}
                            />
                          )}
                        </SoftBox>
                      </SoftBox>
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </SoftBox>

          {selected && (
            <SoftBox mt={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <SoftBox flex={1}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Quantity
                  </SoftTypography>
                  <KendoInput
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                  />
                  {insufficient && (
                    <SoftTypography variant="caption" color="warning" display="block">
                      Quantity exceeds {selected.availableQuantity} on hand. Saving is still allowed.
                    </SoftTypography>
                  )}
                </SoftBox>
                <SoftBox flex={1}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Unit Cost
                  </SoftTypography>
                  <SoftBox
                    sx={{
                      bgcolor: "#fafafa",
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                      border: "1px solid #eee",
                    }}
                  >
                    <SoftTypography variant="body2">${rate.toFixed(2)}</SoftTypography>
                  </SoftBox>
                </SoftBox>
              </Stack>
            </SoftBox>
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end" mt={3}>
            <SoftButton variant="outlined" onClick={handleClose}>
              Cancel
            </SoftButton>
            <SoftButton color="success" variant="contained" onClick={handleAdd} disabled={!selected}>
              Add Part
            </SoftButton>
          </Stack>
        </Card>
      </SoftBox>
    </Modal>
  );
};

AddPartPickerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onAddItem: PropTypes.func.isRequired,
};

export default AddPartPickerModal;
